/**
 * @fileoverview Schemas Zod que reflejan los contratos del backend
 * (ver `/app/docs/API.md`). Cada respuesta del API se valida con su
 * schema correspondiente antes de usarse, para detectar drift entre
 * contratos.
 */

import { z } from "zod";

/* ---------- enums ---------- */

export const ValorVoto = z.enum(["acuerdo", "desacuerdo", "pasar"]);
export const EdadRango = z.enum(["18-29", "30-44", "45-59", "60+"]);
export const Genero = z.enum(["mujer", "hombre", "otro", "prefiero_no_decir"]);
export const Grupo = z.enum([
    "ninguno",
    "persona_con_discapacidad",
    "persona_mayor",
    "comunidad_originaria",
]);

/* ---------- POST /tokens/validate ---------- */

export const TokenValidateResponse = z.object({
    valid: z.boolean(),
    forum_id: z.string().nullable().optional(),
    alcaldia_foro: z.string().nullable().optional(),
    estado: z.string().nullable().optional(),
    puede_reanudar: z.boolean().nullable().optional(),
    participacion_id: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
    mensaje: z.string().nullable().optional(),
});

/* ---------- POST /participaciones/start ---------- */

export const DatosParticipante = z.object({
    alcaldia_residencia: z.string().length(3).nullable().optional(),
    edad_rango: EdadRango.nullable().optional(),
    genero: Genero.nullable().optional(),
    grupo: Grupo.nullable().optional(),
});

export const ParticipacionStartResponse = z.object({
    session_token: z.string(),
    participacion_id: z.string(),
    expira_en: z.string(),
});

/* ---------- POST /participaciones/resume ---------- */

export const VotoRead = z.object({
    statement_id: z.string(),
    valor: ValorVoto,
    fecha: z.string(),
});

export const ParticipacionResumeResponse = z.object({
    session_token: z.string(),
    participacion_id: z.string(),
    expira_en: z.string(),
    votos_previos: z.array(VotoRead),
});

/* ---------- GET /brechas ---------- */

export const StatementPublic = z.object({
    id: z.string(),
    codigo: z.string(),
    texto: z.string(),
});

export const BrechaPublic = z.object({
    id: z.number(),
    numero: z.number(),
    titulo_ciudadano: z.string(),
    descripcion_corta: z.string(),
    statements: z.array(StatementPublic),
});

export const BrechasResponse = z.array(BrechaPublic);

/* ---------- POST /votos ---------- */

export const VotoCreateResponse = z.object({
    ok: z.boolean(),
    voto_id: z.string(),
});

/* ---------- GET /participaciones/me/votos ---------- */

export const VotosMeResponse = z.array(VotoRead);

/* ---------- POST /respuestas_abiertas ---------- */

export const RespuestaAbiertaResponse = z.object({
    ok: z.boolean(),
});

/* ---------- POST /participaciones/complete ---------- */

export const ParticipacionCompleteResponse = z.object({
    ok: z.boolean(),
    fecha_fin: z.string(),
});

/* ---------- type aliases via JSDoc ---------- */

/**
 * @typedef {z.infer<typeof TokenValidateResponse>} TokenValidate
 * @typedef {z.infer<typeof DatosParticipante>} Datos
 * @typedef {z.infer<typeof BrechaPublic>} Brecha
 * @typedef {z.infer<typeof StatementPublic>} Statement
 * @typedef {z.infer<typeof VotoRead>} Voto
 * @typedef {"acuerdo"|"desacuerdo"|"pasar"} ValorVotoTipo
 */
